import React from 'react'
import * as moment from 'moment-timezone'
import autobind from 'autobind-decorator'
import { Moment } from 'moment'
import { Button, DatePicker, Divider, Form, Select, Skeleton, Tooltip } from 'antd'
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { BASE_TIME_FORMAT, SHORT_DATE_FORMAT } from '../../../constants'
import { Container } from 'typescript-ioc/es5'
import { BetterComponent } from '../../../components/better-component/better-component'
import { Well } from '@pushly/aqe/lib/components'
import { AppState } from '../../../stores/app'
import { AppService } from '../../../services'
import { Journey } from '../types/journey-api-response'
import { JourneyGenStatsRow } from './journey-gen-stats-row'
import { JourneySubscriberGraph } from './journey-subscriber-graph'
import { JourneyPerformanceGraph } from './journey-performance-graph'
import { InsightsService } from '../../../services/insights'
import { DomainDto } from '../../../dtos/domain'
import { getDomainCurrencyCode } from '../../../_utils/domain'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import type { RangeValueWithPreset, RangeValueRecordWithPreset } from '../../../types/range-value'

type TimeBreakdown = ReportDateIncrement.DAY | ReportDateIncrement.WEEK | ReportDateIncrement.MONTH

export interface IJourneySummaryTabProps {
    journey: Journey
    domain?: DomainDto
}

export interface IJourneySummaryTabState {
    timeBreakdown: TimeBreakdown
    timeWindowPreset: string
    timeRangeSince: Moment | null
    timeRangeUntil: Moment | null
    loadingLifetimeStats: boolean
    loadingBreakdownStats: boolean
    lifetimeStats: any[]
    breakdownStats: any[]
}

export class JourneySummaryTab extends BetterComponent<IJourneySummaryTabProps, IJourneySummaryTabState> {
    protected static tabName = 'summary'
    protected static tabLabel = 'Summary'

    private appState: AppState
    private appService: AppService
    private insightsService: InsightsService

    private get defaultTimeRange() {
        return this.reportTimeRanges['Last 7 Days']()
    }

    private get reportTimeRanges(): RangeValueRecordWithPreset<Moment> {
        const now = new Date()

        return {
            ['Last 7 Days']: () => [moment(now).subtract(6, 'd'), moment(now), 'Last 7 Days'],
            ['Last 30 Days']: () => [moment(now).subtract(29, 'd'), moment(now), 'Last 30 Days'],
            ['Last 90 Days']: () => [moment(now).subtract(89, 'd'), moment(now), 'Last 90 Days'],
            ['This Month']: () => [moment(now).startOf('month'), moment(now), 'This Month'],
            ['All Time']: () => [null, null, 'All Time'],
        }
    }

    public constructor(props: IJourneySummaryTabProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.insightsService = Container.get(InsightsService)

        const defaultRange = this.defaultTimeRange

        this.state = {
            timeBreakdown: ReportDateIncrement.DAY,
            timeWindowPreset: 'Last 7 Days',
            timeRangeSince: defaultRange[0],
            timeRangeUntil: defaultRange[1],
            loadingLifetimeStats: true,
            loadingBreakdownStats: true,
            lifetimeStats: [],
            breakdownStats: [],
        }
    }

    public async componentDidMount(): Promise<void> {
        if (!this.isViewDisabled()) {
            this.loadStats()
        } else {
            this.setState({ loadingLifetimeStats: false, loadingBreakdownStats: false })
        }
    }

    public render(): React.ReactNode {
        if (this.isViewDisabled()) {
            return (
                <div>
                    <Well className="thin gen-stats-well nested" hideHeader={true} hideFooter={true}>
                        <div className="performance-empty-state">
                            <InfoCircleOutlined className="empty-state-icon" />
                            <span className="empty-state-title">Performance data will be available once the journey begins.</span>
                            <span className="empty-state-subtitle">
                                Scheduled for {moment(this.props.journey.date_start).format(BASE_TIME_FORMAT)}
                            </span>
                        </div>
                    </Well>
                </div>
            )
        }

        return (
            <div>
                <Skeleton active={true} loading={false}>
                    <JourneyGenStatsRow
                        loading={this.state.loadingLifetimeStats}
                        domain={this.appState.currentDomain}
                        journey={this.props.journey}
                        stats={this.state.lifetimeStats}
                    />

                    <Well
                        loading={this.state.loadingBreakdownStats}
                        className="performance-overview-well"
                        title="Performance Overview"
                        actions={this.renderStatsActions()}
                        hideFooter={true}
                    >
                        <JourneySubscriberGraph
                            loading={this.state.loadingBreakdownStats}
                            journey={this.props.journey}
                            stats={this.state.breakdownStats}
                            timeBreakdown={this.state.timeBreakdown}
                        />
                        <JourneyPerformanceGraph
                            loading={this.state.loadingBreakdownStats}
                            journey={this.props.journey}
                            domain={this.appState.currentDomain}
                            stats={this.state.breakdownStats}
                            timeBreakdown={this.state.timeBreakdown}
                        />
                    </Well>
                </Skeleton>
            </div>
        )
    }

    private get isSingleDayBreakdown(): boolean {
        if (!this.state.timeRangeSince) {
            return false
        }
        return this.state.timeRangeUntil?.diff(this.state.timeRangeSince, 'd') === 0
    }

    private get isLifetimeBreakdown(): boolean {
        return !this.state.timeRangeSince
    }

    protected renderStatsActions(): React.ReactNode {
        let dateGroupingLabel: any = 'Date Grouping'

        if (this.isLifetimeBreakdown) {
            dateGroupingLabel = (
                <div>
                    <Tooltip title='Only "Monthly" date grouping is supported when selected timeframe is "All Time".'>
                        Date Grouping
                        <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                </div>
            )
        }

        return (
            <React.Fragment>
                <Form>
                    <Form.Item label={dateGroupingLabel}>
                        <Select
                            size="small"
                            value={this.state.timeBreakdown}
                            onChange={this.setTimeBreakdownState}
                            disabled={this.isSingleDayBreakdown || this.isLifetimeBreakdown}
                        >
                            <Select.Option value={ReportDateIncrement.DAY}>Daily</Select.Option>
                            <Select.Option value={ReportDateIncrement.WEEK}>Weekly</Select.Option>
                            <Select.Option value={ReportDateIncrement.MONTH}>Monthly</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Timeframe">
                        <DatePicker.RangePicker
                            size="small"
                            className="date-picker"
                            dropdownClassName="reporting-date-range"
                            getPopupContainer={this.appService.getAppContainer}
                            format={SHORT_DATE_FORMAT}
                            ranges={this.reportTimeRanges as any}
                            value={
                                this.state.timeRangeSince
                                    ? [this.state.timeRangeSince, this.state.timeRangeUntil]
                                    : [null, null]
                            }
                            onCalendarChange={this.setDateRangeState}
                        />
                    </Form.Item>

                    <Divider type="vertical" orientation="center" className="header-divider" />

                    <Form.Item>
                        <Tooltip title="Refresh">
                            <Button
                                className="refresh-report"
                                size="small"
                                shape="round"
                                onClick={this.refreshBreakdownStats}
                            >
                                <ReloadOutlined spin={this.state.loadingBreakdownStats} />
                            </Button>
                        </Tooltip>
                    </Form.Item>
                </Form>
            </React.Fragment>
        )
    }

    private setDateRangeState = async (value: RangeValueWithPreset<Moment>) => {
        if (value?.[0] === null && value?.[1] === null) {
            // "All Time" selected — force monthly grouping
            await this.setState({ timeBreakdown: ReportDateIncrement.MONTH })
        }

        await this.setState({
            timeRangeSince: value?.[0] ?? null,
            timeRangeUntil: value?.[1] ?? null,
        })

        const preset = value?.[2] ? value[2] : 'CUSTOM'
        await this.setState({ timeWindowPreset: preset })

        if (!!this.state.timeRangeUntil || this.isLifetimeBreakdown) {
            this.loadJourneyBreakdownStats()
        }
    }

    private refreshBreakdownStats = async () => {
        await this.loadJourneyBreakdownStats()
    }

    @autobind
    protected async setTimeBreakdownState(value: TimeBreakdown) {
        await this.setState({ timeBreakdown: value })
        return this.loadJourneyBreakdownStats()
    }

    @autobind
    protected async loadStats(): Promise<any> {
        const statLoaders: Array<Promise<void>> = []

        statLoaders.push(this.loadJourneyLifetimeStats())
        statLoaders.push(this.loadJourneyBreakdownStats())

        return Promise.all(statLoaders)
    }

    @autobind
    protected async loadJourneyLifetimeStats(): Promise<any> {
        await this.setState({
            loadingLifetimeStats: true,
        })

        const stats = await this.insightsService.fetch(
            {
                entity: ReportEntity.CAMPAIGNS,
                date_preset: ReportDatePreset.LIFETIME,
                date_increment: ReportDateIncrement.LIFETIME,
                breakdowns: ['campaign'],
                fields: [
                    'campaign_id',
                    'entrances',
                    'deliveries',
                    'impressions',
                    'clicks',
                    'delivery_rate_decimal',
                    'ctr_decimal',
                    'purchases',
                    'purchase_amount',
                    'currency_code',
                ],
                filters: [
                    {
                        field: 'domain.id',
                        operator: 'eq',
                        value: this.globalDomain!.id,
                    },
                    {
                        field: 'campaign.id',
                        operator: 'eq',
                        value: this.props.journey.id,
                    },
                ],
            },
            {
                cancellationKey: `cl.fetchJourneyStats.${this.props.journey.id}`,
            },
        )

        return this.setState({
            loadingLifetimeStats: false,
            lifetimeStats: Array.isArray(stats) && stats.length > 0 ? stats[0] : {},
        }).then(() => stats)
    }

    @autobind
    protected async loadJourneyBreakdownStats(): Promise<any> {
        await this.setState({
            loadingBreakdownStats: true,
        })

        const contract: any = {
            entity: ReportEntity.CAMPAIGNS,
            date_increment: this.state.timeBreakdown,
            breakdowns: ['campaign'],
            fields: [
                'campaign_id',
                'entrances',
                'deliveries',
                'impressions',
                'delivery_rate',
                'delivery_rate_decimal',
                'clicks',
                'ctr',
                'ctr_decimal',
                'purchases',
                'purchase_amount',
                'currency_code',
            ],
            filters: [
                {
                    field: 'domain.id',
                    operator: 'eq',
                    value: this.globalDomain!.id,
                },
                {
                    field: 'campaign.id',
                    operator: 'eq',
                    value: this.props.journey.id,
                },
            ],
            order: {
                field: 'event_date',
                direction: 'asc',
            },
        }

        if (this.isLifetimeBreakdown) {
            contract.date_preset = ReportDatePreset.LIFETIME
            contract.date_increment = ReportDateIncrement.MONTH
        } else {
            if (this.isSingleDayBreakdown) {
                contract.date_increment = ReportDateIncrement.DAY
            }

            contract.date_range = {
                since: this.state.timeRangeSince!.format('YYYY-MM-DD'),
                through: this.state.timeRangeUntil!.format('YYYY-MM-DD'),
            }
        }

        const stats = await this.insightsService.fetch(contract, {
            cancellationKey: `cl.fetchJourneyBreakdownStats.${this.props.journey.id}`,
        })

        let backfilledStats: any[] = []
        if (stats.length > 0) {
            backfilledStats = [stats[0]]

            const lastStat = stats[stats.length - 1]
            const statDateCap = moment(`${lastStat.event_date} 00:00:00`)
            const currency = getDomainCurrencyCode(this.props.domain)

            let previousStat: any = stats[0]

            while (true) {
                const nextStatDate: Moment = moment(`${previousStat.event_date} 00:00:00`).add(
                    1,
                    this.state.timeBreakdown,
                )
                const nextEventDate = nextStatDate.format('YYYY-MM-DD')

                let nextStat: any = stats.find((s: any) => s.event_date === nextEventDate)
                if (!nextStat) {
                    nextStat = {
                        event_date: nextEventDate,
                        campaign_id: this.props.journey.id,
                    }

                    nextStat = {
                        ...nextStat,
                        entrances: 0,
                        deliveries: 0,
                        impressions: 0,
                        clicks: 0,
                        delivery_rate: 0,
                        ctr: 0,
                        purchases: 0,
                        purchase_count: null,

                        // TODO: replace backfill with domain.currencyCode
                        //  or appropriate column once created and deployed
                        currency_code: currency,
                    }
                }

                if (nextStatDate > statDateCap) {
                    break
                }

                backfilledStats.push(nextStat)
                previousStat = nextStat
            }
        }

        return this.setState({
            loadingBreakdownStats: false,
            breakdownStats: backfilledStats,
        }).then(() => backfilledStats)
    }

    protected isViewDisabled(): boolean {
        const isCancelled = this.props.journey.status === 'CANCELLED'

        const now: Moment = moment()
        const dateStart: Moment = moment(this.props.journey.date_start)
        const isScheduled = dateStart > now

        return isCancelled || isScheduled
    }
}
